/**
 * Hook to synchronize language preferences between i18next and Supabase
 */

import { useEffect, useRef } from 'react';
import i18n from '@/lib/i18n';
import { updateUserPreferences, getUserPreferences } from '@/lib/user-preferences-service';
import type { LanguagePreference } from '@/lib/user-preferences';

export function useLanguageSync() {
  const hasLoadedFromDatabase = useRef(false);

  // Load language from database on mount (only once)
  useEffect(() => {
    if (hasLoadedFromDatabase.current) return;

    const loadLanguageFromDatabase = async () => {
      try {
        const preferences = await getUserPreferences();
        
        if (preferences?.language) {
          // Set language from database if available
          await i18n.changeLanguage(preferences.language);
        } else {
          // Default to Polish if no saved preference or user not logged in
          const defaultLanguage: LanguagePreference = 'pl';
          await i18n.changeLanguage(defaultLanguage);
          
          // Try to save default language to database (will fail silently if user not logged in)
          try {
            await updateUserPreferences({ language: defaultLanguage });
          } catch (saveError) {
            // Silently fail if user is not authenticated
            console.debug('[useLanguageSync] Could not save default language (user may not be logged in)');
          }
        }
        hasLoadedFromDatabase.current = true;
      } catch (error) {
        console.error('[useLanguageSync] Error loading language from database:', error);
        // Fallback to Polish on any error
        await i18n.changeLanguage('pl');
        hasLoadedFromDatabase.current = true; // Mark as loaded even on error to prevent retries
      }
    };

    loadLanguageFromDatabase();
  }, []);

  return null;
}
